import { useCallback, useState } from 'react';
import { cx } from '@linaria/core';
import dynamic from 'next/dynamic';
import { extractMediaUrl } from 'utils/inputs';
import { TSupportedPredictionCategories } from 'modules/Predictions/types';
import { observer } from 'mobx-react-lite';
import { useUnsafePredictionStore } from 'modules/Predictions/contexts/MstPredictionStoreCtx';
import { TextInputView } from 'components/TextInputView';
import { Spinner } from 'components/Spinner/Spinner';
import type { PredictionViewConfig } from 'modules/Predictions/types';
import { VideoControls } from 'modules/Canvas/components/VideoControls';
import { withGuaranteedPredStore } from 'modules/Predictions/withGuaranteedPredStore';
import { StoreProvider } from 'modules/VideoTrackTimeline/contexts/StoreProvider';
import type { CanvasProps } from '../PredictionCanvas';
import { mainPanelWrapper, canvasWrapper, videoControllerWrapper } from './styles';

const PredictionCanvas = dynamic<CanvasProps>(
  import('modules/Predictions/components/PredictionCanvas/PredictionCanvas').then((x) => x.PredictionCanvas),
  { ssr: false },
);

const unsupportedInputText = 'Unsupported Input';

/**
 * Renders Canvas ***NEEDS*** MediaStateProvider as parent,
 * and is ideally a child of PredictionView lest useful otherwise
 * */
export const PredictionMedia: React.FC<{
  canvas: PredictionViewConfig['canvas'];
  measureRef: React.LegacyRef<HTMLDivElement>;
  width: number;
  height: number;
}> = withGuaranteedPredStore(
  observer(({ canvas, measureRef, width, height }) => {
    const [isFullScreenCanvasEnabled, setFullScreenCanvasEnabled] = useState(false);

    const store = useUnsafePredictionStore();

    const { inputList, currentInput } = store.media;
    const onFullScreenToggle = useCallback(() => {
      setFullScreenCanvasEnabled(!isFullScreenCanvasEnabled);
    }, [isFullScreenCanvasEnabled]);

    return (
      <div className={cx(mainPanelWrapper, isFullScreenCanvasEnabled && 'fullscreen-canvas')} ref={measureRef}>
        <div className={cx('canvasWrapper', canvasWrapper, isFullScreenCanvasEnabled && 'full-height')}>
          {(function chooseMediaRenderer() {
            if (!inputList.length || !currentInput) {
              return 'No input available.';
            }

            if (store.renderingCategory === TSupportedPredictionCategories.Initialising) return <Spinner />;

            if (!Object.values(TSupportedPredictionCategories).includes(store.renderingCategory)) {
              return 'Web-based predictions are not supported for this type of models. Please use Clarifai API.';
            }

            if (
              [TSupportedPredictionCategories.Image, TSupportedPredictionCategories.ImageJson, TSupportedPredictionCategories.Any].includes(
                store.renderingCategory,
              ) &&
              ('image' in currentInput.data || 'video' in currentInput.data)
            ) {
              const url = extractMediaUrl(currentInput as CF.API.Inputs.ImageInput);
              if (!url.length) return unsupportedInputText;

              // Image / Video Input View
              return (
                <div className={cx('canvas-parent')}>
                  <PredictionCanvas
                    canvas={canvas}
                    key={url}
                    store={store}
                    src={url}
                    size={{ width, height }}
                    mediaType={store.media.currentInputType}
                  />
                </div>
              );
            }

            if (
              [TSupportedPredictionCategories.Text, TSupportedPredictionCategories.TextJson].includes(store.renderingCategory) &&
              'text' in currentInput.data
            ) {
              const input = currentInput as CF.API.Inputs.TextInput;
              // Text Input View
              return <TextInputView input={input} />;
            }

            if (
              [TSupportedPredictionCategories.Audio, TSupportedPredictionCategories.AudioJson].includes(store.renderingCategory) &&
              'audio' in currentInput.data
            ) {
              const source =
                'url' in currentInput.data.audio ? currentInput.data.audio.url : `data:audio/mpeg;base64,${currentInput.data.audio.base64}`;
              return (
                <audio controls key={'url' in currentInput.data.audio ? source : source.slice(0, 200)}>
                  <track kind="captions" src={source} />
                  <source src={source} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              );
            }
            // Unsupported input type
            return <div>Media rendering not supported, you can view the raw output JSON instead.</div>;
          })()}
        </div>
        {/* Video support to be added after initial new arch implementation */}
        {currentInput && 'video' in currentInput.data && (
          <StoreProvider>
            <div className={videoControllerWrapper}>
              <VideoControls
                onCurrentFrameChanged={store.viewState.setCurrentSecond}
                onFullScreenToggle={onFullScreenToggle}
                videoMetadata={currentInput.data.metadata}
              />
            </div>
          </StoreProvider>
        )}
      </div>
    );
  }),
);
